import React, { Suspense, lazy, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { useAuth } from './hooks/useAuth';
import { loadItemCategories } from 'src/settings/core/items';
import { useThemeSettings } from 'src/context/themeSettingsContext';
import { SSEProvider } from 'src/context/SSEContext';

const HomeRouter = lazy(() => import('./routes/homeRoutes'));
const AuthRouter = lazy(() => import('./routes/authRoutes'));

function LoadingScreen({ message }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="primary" size={48} thickness={4} />
        {message ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {message}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const { hydrateFromBackend } = useThemeSettings();

  // Early prefetch of item categories for faster first modal open
  useEffect(() => {
    loadItemCategories().catch(() => {
      // silent: modal/UI will fallback if offline
    });
  }, []);

  // Load theme and typography settings after authentication
  useEffect(() => {
    if (user?.email) {
      hydrateFromBackend();
    }
  }, [user, hydrateFromBackend]);

  return (
    <>
      <ToastContainer position="top-right" newestOnTop closeOnClick pauseOnHover theme="colored" />
      {loading ? (
        <LoadingScreen message="Preparing your workspace..." />
      ) : (
        <Suspense fallback={<LoadingScreen message="Loading CRM interface..." />}>
          {user?.email ? (
            <SSEProvider>
              <HomeRouter />
            </SSEProvider>
          ) : (
            <AuthRouter />
          )}
        </Suspense>
      )}
    </>
  );
}
